import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import DocumentPreviewDialog from '../DocumentPreviewDialog';
import { useAuth } from '../../contexts/AuthContext';
import {
  Upload, Download, Send, Undo2, Trash2, UserPlus, Eye, FileText, RefreshCw,
  CheckCircle2, AlertTriangle, Clock, Search, Loader2, Server
} from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUT_LABELS = {
  a_verifier: { label: 'À vérifier', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' },
  distribue: { label: 'Distribué', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' },
  rejete: { label: 'Rejeté', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200' },
};

const base64ToBlobUrl = (content, mimetype = 'application/pdf') => {
  const byteCharacters = atob(content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimetype });
  return URL.createObjectURL(blob);
};

/**
 * Onglet « Bulletins » de la préparation paie.
 * Workflow : import (logiciel de paie ou dépôt PDF) -> vérification / affectation -> distribution
 * par une personne disposant de la permission bulletins_distribute.
 */
const BulletinsTab = ({ periode, employees = [], payrollConfig, capabilities, providerName, onImportStarted }) => {
  // Capacités du logiciel de paie : l'import automatique n'est proposé que si le logiciel renvoie les bulletins
  // (capacité payslips.amounts) ; sinon seul le dépôt de PDF est disponible.
  const providerCapabilities = capabilities || payrollConfig?.capabilities || [];
  const canImportFromProvider = !!payrollConfig?.payroll_provider && providerCapabilities.includes('payslips.amounts');
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('payroll_edit');
  const canDistribute = hasPermission('bulletins_distribute');

  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatut, setFilterStatut] = useState('all');
  const [scope, setScope] = useState('periode'); // periode | annee | tous
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);

  // Import depuis le logiciel de paie (tâche de fond)
  const [importJob, setImportJob] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const pollRef = useRef(null);

  // Dépôt manuel
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadPeriode, setUploadPeriode] = useState(periode);
  const [uploadEmployeeId, setUploadEmployeeId] = useState('');
  const [uploading, setUploading] = useState(false);

  // Affectation
  const [affectTarget, setAffectTarget] = useState(null);
  const [affectEmployeeId, setAffectEmployeeId] = useState('');
  const [affectPeriode, setAffectPeriode] = useState('');
  const [affectSearch, setAffectSearch] = useState('');

  // Distribution
  const [distribTargets, setDistribTargets] = useState(null);
  const [distribEmail, setDistribEmail] = useState(false);
  const [distribMessage, setDistribMessage] = useState('');
  const [distributing, setDistributing] = useState(false);

  const [preview, setPreview] = useState(null);

  const fetchBulletins = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (scope === 'periode') params.set('periode', periode);
      else if (scope === 'annee') params.set('annee', periode.slice(0, 4));
      if (filterStatut !== 'all') params.set('statut', filterStatut);
      const res = await axios.get(`${API_URL}/bulletins-paie?${params.toString()}`);
      setBulletins(Array.isArray(res.data) ? res.data : []);
      setSelected([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les bulletins');
    } finally {
      setLoading(false);
    }
  }, [periode, scope, filterStatut]);

  useEffect(() => { fetchBulletins(); }, [fetchBulletins]);
  useEffect(() => { setUploadPeriode(periode); }, [periode]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ---- Import depuis le logiciel de paie ----
  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/payroll/import-jobs/${jobId}`);
        setImportJob(res.data);
        if (res.data.statut !== 'en_cours') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setImportResult({ ...(res.data.resultat || {}), erreur: res.data.erreur, provider: res.data.provider_name, periode: res.data.periode });
          setImportJob(null);
          fetchBulletins();
          if (res.data.statut === 'erreur') toast.error(res.data.erreur || 'Import en erreur');
          else toast.success('Import terminé');
        }
      } catch (error) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setImportJob(null);
        toast.error('Suivi de l\'import interrompu');
      }
    }, 2000);
  };

  const handleImportFromProvider = async () => {
    if (!payrollConfig?.payroll_configured) {
      toast.error('Aucun logiciel de paie configuré. Utilisez le dépôt manuel ou configurez l\'intégration dans Administration > Paie.');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/payroll/import-payslips?periode=${periode}`);
      setImportJob({ id: res.data.job_id, statut: 'en_cours', total: 0, traites: 0, provider_name: res.data.provider });
      if (onImportStarted) onImportStarted();
      pollJob(res.data.job_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du lancement de l\'import');
    }
  };

  // ---- Dépôt manuel ----
  const handleUpload = async () => {
    if (uploadFiles.length === 0) { toast.error('Sélectionnez au moins un fichier PDF ou ZIP'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      uploadFiles.forEach((f) => form.append('files', f));
      if (uploadPeriode) form.append('periode', uploadPeriode);
      if (uploadEmployeeId) form.append('employee_id', uploadEmployeeId);
      const res = await axios.post(`${API_URL}/bulletins-paie/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setIsUploadOpen(false);
      setUploadFiles([]);
      setUploadEmployeeId('');
      setImportResult({ ...res.data, provider: 'Dépôt manuel' });
      fetchBulletins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du dépôt');
    } finally {
      setUploading(false);
    }
  };

  // ---- Affectation ----
  const openAffect = (bulletin) => {
    setAffectTarget(bulletin);
    setAffectEmployeeId(bulletin.employee_id || '');
    setAffectPeriode(bulletin.periode || periode);
    setAffectSearch('');
  };

  const handleAffect = async () => {
    if (!affectEmployeeId) { toast.error('Choisissez un salarié'); return; }
    try {
      await axios.put(`${API_URL}/bulletins-paie/${affectTarget.id}/affecter`, { employee_id: affectEmployeeId, periode: affectPeriode });
      toast.success('Bulletin affecté');
      setAffectTarget(null);
      fetchBulletins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'affectation');
    }
  };

  // ---- Distribution / retrait / suppression ----
  const openDistribute = (ids) => {
    const targets = bulletins.filter((b) => ids.includes(b.id) && b.employee_id && b.statut !== 'distribue');
    if (targets.length === 0) { toast.info('Aucun bulletin distribuable dans la sélection (déjà distribué ou non affecté)'); return; }
    setDistribTargets(targets);
    setDistribEmail(false);
    setDistribMessage('');
  };

  const handleDistribute = async () => {
    setDistributing(true);
    try {
      const res = await axios.post(`${API_URL}/bulletins-paie/distribuer`, {
        ids: distribTargets.map((b) => b.id), notifier_email: distribEmail, message: distribMessage || null,
      });
      toast.success(`${res.data.distribues} bulletin(s) distribué(s)`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} bulletin(s) non distribué(s)`);
      setDistribTargets(null);
      fetchBulletins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la distribution');
    } finally {
      setDistributing(false);
    }
  };

  const handleRetirer = async (ids) => {
    if (!window.confirm(`Retirer ${ids.length} bulletin(s) de la distribution ? Les salariés ne les verront plus.`)) return;
    try {
      const res = await axios.post(`${API_URL}/bulletins-paie/retirer`, { ids });
      toast.success(`${res.data.retires} bulletin(s) retiré(s)`);
      fetchBulletins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du retrait');
    }
  };

  const handleDelete = async (bulletin) => {
    if (!window.confirm(`Supprimer définitivement ce bulletin (${bulletin.periode_label || bulletin.periode}) ?`)) return;
    try {
      await axios.delete(`${API_URL}/bulletins-paie/${bulletin.id}`);
      toast.success('Bulletin supprimé');
      fetchBulletins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const fetchPdf = async (bulletin) => {
    const res = await axios.get(`${API_URL}/bulletins-paie/${bulletin.id}/pdf`);
    if (!res.data.content) throw new Error('vide');
    return { url: base64ToBlobUrl(res.data.content), filename: res.data.filename };
  };

  const handlePreview = async (bulletin) => {
    try {
      const { url, filename } = await fetchPdf(bulletin);
      setPreview({ data: url, filename, mimetype: 'application/pdf', bulletin });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF non disponible');
    }
  };

  const handleDownload = async (bulletin) => {
    try {
      const { url, filename } = await fetchPdf(bulletin);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF non disponible');
    }
  };

  // ---- Sélection ----
  const visible = bulletins.filter((b) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (b.employee_name || '').toLowerCase().includes(s)
      || (b.employee_matricule || '').toLowerCase().includes(s)
      || (b.fichier_nom || '').toLowerCase().includes(s)
      || (b.periode || '').includes(s);
  });
  const allVisibleSelected = visible.length > 0 && visible.every((b) => selected.includes(b.id));
  const toggleAll = () => setSelected(allVisibleSelected ? [] : visible.map((b) => b.id));
  const toggleOne = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const counts = bulletins.reduce((acc, b) => {
    const key = !b.employee_id ? 'non_rapproche' : b.statut;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const filteredEmployees = employees.filter((e) => {
    if (!affectSearch) return true;
    const s = affectSearch.toLowerCase();
    return `${e.nom} ${e.prenom} ${e.matricule_paie || ''} ${e.matricule || ''}`.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4" data-testid="bulletins-tab">
      {/* Barre d'actions */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" /> À vérifier : {counts.a_verifier || 0}</Badge>
              <Badge variant="outline" className="gap-1 text-amber-700"><UserPlus className="w-3 h-3" /> À affecter : {counts.non_rapproche || 0}</Badge>
              <Badge variant="outline" className="gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Distribués : {counts.distribue || 0}</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <Button variant="outline" className="gap-2" onClick={() => setIsUploadOpen(true)} data-testid="upload-bulletins-btn">
                  <Upload className="w-4 h-4" /> Déposer des PDF
                </Button>
              )}
              {canEdit && canImportFromProvider && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={handleImportFromProvider}
                  disabled={!!importJob || !payrollConfig?.payroll_configured}
                  title={payrollConfig?.payroll_configured ? `Importer depuis ${providerName}` : `Configuration de ${providerName} incomplète`}
                  data-testid="import-bulletins-btn"
                >
                  {importJob ? <Loader2 className="w-4 h-4 animate-spin" /> : <Server className="w-4 h-4" />}
                  {importJob ? `Import en cours${importJob.total ? ` (${importJob.traites || 0}/${importJob.total})` : '...'}` : `Importer depuis ${providerName || 'le logiciel de paie'}`}
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={fetchBulletins} title="Actualiser"><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-9" placeholder="Rechercher un salarié, un matricule, un fichier..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-full md:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="periode">Période sélectionnée</SelectItem>
                <SelectItem value="annee">Année {periode.slice(0, 4)}</SelectItem>
                <SelectItem value="tous">Toutes les périodes</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-full md:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="a_verifier">À vérifier</SelectItem>
                <SelectItem value="non_rapproche">À affecter</SelectItem>
                <SelectItem value="distribue">Distribués</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selected.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-2 rounded-md bg-slate-50 dark:bg-slate-800/60 border">
              <span className="text-sm font-medium">{selected.length} sélectionné(s)</span>
              {canDistribute && (
                <Button size="sm" className="gap-1 bg-[#0F5156] hover:bg-[#0c4145] text-white" onClick={() => openDistribute(selected)}>
                  <Send className="w-3.5 h-3.5" /> Distribuer
                </Button>
              )}
              {canDistribute && (
                <Button size="sm" variant="outline" className="gap-1" onClick={() => handleRetirer(selected.filter((id) => bulletins.find((b) => b.id === id)?.statut === 'distribue'))}>
                  <Undo2 className="w-3.5 h-3.5" /> Retirer de la distribution
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Annuler</Button>
            </div>
          )}

          {!canDistribute && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Vous pouvez vérifier et affecter les bulletins ; la distribution aux salariés est réservée aux rôles disposant de la permission « Distribuer les bulletins de paie ».
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll} aria-label="Tout sélectionner" /></TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Salarié</TableHead>
                  <TableHead className="text-right">Brut</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Distribution</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Chargement...</TableCell></TableRow>
                ) : visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-10 text-slate-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      Aucun bulletin pour cette sélection.
                      {canEdit && <span className="block text-xs mt-1">Déposez des PDF ou lancez un import depuis votre logiciel de paie.</span>}
                    </TableCell>
                  </TableRow>
                ) : visible.map((b) => {
                  const statut = STATUT_LABELS[b.statut] || STATUT_LABELS.a_verifier;
                  const nonAffecte = !b.employee_id;
                  return (
                    <TableRow key={b.id} className={nonAffecte ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}>
                      <TableCell><Checkbox checked={selected.includes(b.id)} onCheckedChange={() => toggleOne(b.id)} aria-label="Sélectionner" /></TableCell>
                      <TableCell className="font-medium whitespace-nowrap">{b.periode_label || b.periode}</TableCell>
                      <TableCell>
                        {nonAffecte ? (
                          <div>
                            <span className="text-amber-700 font-medium flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> À affecter</span>
                            <span className="text-xs text-slate-500">{b.fichier_nom}{b.indices?.nom_complet ? ` · ${b.indices.nom_complet}` : ''}{b.indices?.matricule ? ` · mat. ${b.indices.matricule}` : ''}</span>
                          </div>
                        ) : (
                          <div>
                            <span className="font-medium">{b.employee_name}</span>
                            <span className="block text-xs text-slate-500">{b.employee_matricule ? `Mat. ${b.employee_matricule}` : ''}{b.fichier_nom ? ` · ${b.fichier_nom}` : ''}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{b.salaire_brut ? formatCurrency(b.salaire_brut) : <span className="text-slate-400">-</span>}</TableCell>
                      <TableCell className="text-right font-mono">{b.salaire_net ? formatCurrency(b.salaire_net) : <span className="text-slate-400">-</span>}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{b.source === 'upload' ? 'Dépôt' : (b.source || '').toUpperCase()}</Badge></TableCell>
                      <TableCell><Badge className={`text-xs ${statut.className}`}>{statut.label}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {b.statut === 'distribue' && b.distribue_le ? (
                          <span>{new Date(b.distribue_le).toLocaleDateString('fr-FR')}<br />{b.distribue_par_email || ''}</span>
                        ) : (b.imported_at ? `Importé le ${new Date(b.imported_at).toLocaleDateString('fr-FR')}` : '-')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button size="icon" variant="ghost" title="Aperçu" onClick={() => handlePreview(b)} disabled={!b.has_pdf}><Eye className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" title="Télécharger" onClick={() => handleDownload(b)} disabled={!b.has_pdf}><Download className="w-4 h-4" /></Button>
                          {canEdit && b.statut !== 'distribue' && (
                            <Button size="icon" variant="ghost" title={nonAffecte ? 'Affecter à un salarié' : 'Réaffecter'} onClick={() => openAffect(b)}><UserPlus className="w-4 h-4" /></Button>
                          )}
                          {canDistribute && b.statut !== 'distribue' && b.employee_id && (
                            <Button size="icon" variant="ghost" className="text-emerald-700" title="Distribuer" onClick={() => openDistribute([b.id])}><Send className="w-4 h-4" /></Button>
                          )}
                          {canDistribute && b.statut === 'distribue' && (
                            <Button size="icon" variant="ghost" className="text-amber-700" title="Retirer de la distribution" onClick={() => handleRetirer([b.id])}><Undo2 className="w-4 h-4" /></Button>
                          )}
                          {canDistribute && (
                            <Button size="icon" variant="ghost" className="text-red-600" title="Supprimer" onClick={() => handleDelete(b)}><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialogue dépôt manuel */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Déposer des bulletins de paie</DialogTitle>
            <DialogDescription>
              PDF individuels ou archive ZIP. Le rapprochement se fait automatiquement à partir du nom du fichier
              (matricule, nom, période) puis du contenu du PDF. Les fichiers non reconnus restent « à affecter ».
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fichiers (PDF ou ZIP)</Label>
              <Input type="file" accept=".pdf,.zip,application/pdf,application/zip" multiple onChange={(e) => setUploadFiles(Array.from(e.target.files || []))} />
              {uploadFiles.length > 0 && <p className="text-xs text-slate-500 mt-1">{uploadFiles.length} fichier(s) sélectionné(s)</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Période par défaut</Label>
                <Input type="month" value={uploadPeriode} onChange={(e) => setUploadPeriode(e.target.value)} />
                <p className="text-[11px] text-slate-500 mt-1">Utilisée si la période n'est pas lisible dans le fichier.</p>
              </div>
              <div>
                <Label>Salarié (optionnel)</Label>
                <Select value={uploadEmployeeId || 'auto'} onValueChange={(v) => setUploadEmployeeId(v === 'auto' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Rapprochement automatique" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="auto">Rapprochement automatique</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.nom} {e.prenom}{e.matricule_paie ? ` (${e.matricule_paie})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Forcer l'affectation de tous les fichiers à ce salarié.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsUploadOpen(false)}>Annuler</Button>
              <Button className="bg-[#0F5156] hover:bg-[#0c4145] text-white gap-2" onClick={handleUpload} disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Dépôt en cours...' : 'Déposer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogue résultat d'import */}
      <Dialog open={!!importResult} onOpenChange={(o) => { if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.erreur ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
              Résultat de l'import {importResult?.provider ? `· ${importResult.provider}` : ''}
            </DialogTitle>
            {importResult?.periode && <DialogDescription>Période {importResult.periode}</DialogDescription>}
          </DialogHeader>
          {importResult && (
            <div className="space-y-4 text-sm">
              {importResult.erreur && (
                <div className="p-3 rounded-md bg-red-50 text-red-800 border border-red-200">{importResult.erreur}</div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20"><p className="text-2xl font-bold text-emerald-700">{importResult.imported_count || 0}</p><p className="text-xs">nouveaux</p></div>
                <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-900/20"><p className="text-2xl font-bold text-blue-700">{importResult.updated_count || 0}</p><p className="text-xs">mis à jour</p></div>
                <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-900/20"><p className="text-2xl font-bold text-amber-700">{importResult.unmatched_count || 0}</p><p className="text-xs">à affecter</p></div>
                <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20"><p className="text-2xl font-bold text-red-700">{importResult.error_count || 0}</p><p className="text-xs">erreurs</p></div>
              </div>
              {importResult.conges_sync && <p className="text-xs text-slate-500">Compteurs de congés synchronisés : {importResult.conges_sync.synced}</p>}
              {(importResult.unmatched || []).length > 0 && (
                <div>
                  <p className="font-medium mb-1">Bulletins à affecter manuellement</p>
                  <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
                    {importResult.unmatched.map((u, i) => (
                      <li key={i}>• {u.fichier || u.nom || u.matricule || 'Bulletin'} {u.matricule && !u.fichier ? `(mat. ${u.matricule})` : ''} {u.error ? `— ${u.error}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(importResult.errors || []).length > 0 && (
                <div>
                  <p className="font-medium mb-1 text-red-700">Erreurs</p>
                  <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
                    {importResult.errors.map((e, i) => (
                      <li key={i}>• {e.fichier || e.matricule || e.id || ''} — {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-xs text-slate-500">Les bulletins importés sont au statut « à vérifier ». Après contrôle, une personne habilitée les distribue dans les dossiers salariés.</p>
              <div className="flex justify-end"><Button onClick={() => setImportResult(null)}>Fermer</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialogue affectation */}
      <Dialog open={!!affectTarget} onOpenChange={(o) => { if (!o) setAffectTarget(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Affecter le bulletin à un salarié</DialogTitle>
            {affectTarget && <DialogDescription>{affectTarget.fichier_nom || `Bulletin ${affectTarget.periode}`}{affectTarget.indices?.nom_complet ? ` · indice : ${affectTarget.indices.nom_complet}` : ''}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Période</Label>
              <Input type="month" value={affectPeriode} onChange={(e) => setAffectPeriode(e.target.value)} />
            </div>
            <div>
              <Label>Salarié</Label>
              <Input placeholder="Filtrer..." value={affectSearch} onChange={(e) => setAffectSearch(e.target.value)} className="mb-2" />
              <div className="max-h-56 overflow-y-auto border rounded-md divide-y">
                {filteredEmployees.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setAffectEmployeeId(e.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${affectEmployeeId === e.id ? 'bg-[#0F5156]/10 font-medium' : ''}`}
                  >
                    {e.nom} {e.prenom} <span className="text-xs text-slate-500">{e.matricule_paie ? `· mat. ${e.matricule_paie}` : ''}{e.departement ? ` · ${e.departement}` : ''}</span>
                  </button>
                ))}
                {filteredEmployees.length === 0 && <p className="p-3 text-xs text-slate-500">Aucun salarié</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAffectTarget(null)}>Annuler</Button>
              <Button className="bg-[#0F5156] hover:bg-[#0c4145] text-white" onClick={handleAffect}>Affecter</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogue distribution */}
      <Dialog open={!!distribTargets} onOpenChange={(o) => { if (!o) setDistribTargets(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-[#0F5156]" /> Distribuer {distribTargets?.length} bulletin(s)</DialogTitle>
            <DialogDescription>
              Les bulletins deviennent visibles dans le dossier de chaque salarié et dans son portail. Une notification leur est envoyée.
            </DialogDescription>
          </DialogHeader>
          {distribTargets && (
            <div className="space-y-3">
              <ul className="text-sm max-h-40 overflow-y-auto border rounded-md divide-y">
                {distribTargets.map((b) => (
                  <li key={b.id} className="px-3 py-1.5 flex justify-between"><span>{b.employee_name}</span><span className="text-slate-500">{b.periode_label || b.periode}</span></li>
                ))}
              </ul>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={distribEmail} onCheckedChange={(v) => setDistribEmail(!!v)} />
                Envoyer aussi un email aux salariés
              </label>
              <div>
                <Label>Message (optionnel)</Label>
                <Textarea rows={2} placeholder="Votre bulletin de paie est disponible dans votre espace personnel." value={distribMessage} onChange={(e) => setDistribMessage(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDistribTargets(null)}>Annuler</Button>
                <Button className="bg-[#0F5156] hover:bg-[#0c4145] text-white gap-2" onClick={handleDistribute} disabled={distributing}>
                  {distributing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Confirmer la distribution
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DocumentPreviewDialog
        preview={preview}
        onClose={() => { if (preview?.data) URL.revokeObjectURL(preview.data); setPreview(null); }}
        onDownload={preview ? () => handleDownload(preview.bulletin) : undefined}
      />
    </div>
  );
};

export default BulletinsTab;
